import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { RefreshCw, PackageOpen, Truck, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IncompletePOPage({ user }) {
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grnPr, setGrnPr] = useState(null);
  const [grnForm, setGrnForm] = useState({ quantity_received: 1, invoice_ref: '', delivery_note: '', received_date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/purchase-requisitions/incomplete`, { headers: { Authorization: `Bearer ${token}` } });
      setPrs(res.data);
    } catch {
      toast.error('Failed to load incomplete POs');
    } finally {
      setLoading(false);
    }
  };

  const openGrn = (pr) => {
    const totalReceived = (pr.grn_entries || []).reduce((s, e) => s + (e.quantity_received || 0), 0);
    const remaining = (pr.quantity_ordered || pr.quantity_requested) - totalReceived;
    setGrnPr(pr);
    setGrnForm({ quantity_received: Math.max(remaining, 1), invoice_ref: '', delivery_note: '', received_date: new Date().toISOString().split('T')[0], notes: '' });
  };

  const handleGrn = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/purchase-requisitions/${grnPr.id}/grn`, grnForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setGrnPr(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record inward');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Incomplete PO Items</h1>
          <p className="text-slate-600 mt-1">Purchase orders with pending / partially received deliveries</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-incomplete">
          <RefreshCw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </div>

      {prs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <PackageOpen className="h-14 w-14 mb-3 opacity-40" />
          <p className="text-lg font-medium">All POs are fully received</p>
        </div>
      ) : (
        <div className="space-y-4">
          {prs.map(pr => {
            const totalReceived = (pr.grn_entries || []).reduce((s, e) => s + (e.quantity_received || 0), 0);
            const ordered = pr.quantity_ordered || pr.quantity_requested;
            const remaining = ordered - totalReceived;
            const pct = ordered > 0 ? Math.round((totalReceived / ordered) * 100) : 0;

            return (
              <div key={pr.id} className="bg-white rounded-lg border shadow-sm p-4" data-testid={`incomplete-po-${pr.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-500">{pr.po_number || pr.pr_number}</span>
                      <Badge className="bg-orange-100 text-orange-800 text-xs">
                        {remaining > 0 ? `${remaining} units pending` : 'Awaiting confirmation'}
                      </Badge>
                      {pr.urgency === 'critical' && <Badge className="bg-red-100 text-red-800 text-xs"><AlertTriangle className="h-3 w-3 mr-1 inline" />Critical</Badge>}
                    </div>
                    <p className="font-semibold text-slate-900 mt-1">{pr.part_name}</p>
                    {pr.part_number && <p className="text-xs text-slate-400">{pr.part_number}</p>}
                    <div className="flex gap-4 mt-1 text-xs text-slate-500">
                      {pr.door_no && <span>Asset: {pr.door_no}</span>}
                      {pr.vendor_name && <span>Vendor: {pr.vendor_name}</span>}
                      <span>Ordered: {format(new Date(pr.ordered_at || pr.created_at), 'dd MMM yyyy')}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Received / Ordered</p>
                      <p className="text-lg font-bold text-slate-900">{totalReceived} <span className="text-slate-400 font-normal">/ {ordered}</span></p>
                    </div>
                    {/* Progress bar */}
                    <div className="w-32 bg-slate-100 rounded-full h-2">
                      <div className="bg-green-500 rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-slate-500">{pct}% received</p>
                    {remaining > 0 && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 mt-1" onClick={() => openGrn(pr)} data-testid={`record-inward-${pr.id}`}>
                        <Truck className="h-3.5 w-3.5 mr-1.5" />Record Inward
                      </Button>
                    )}
                  </div>
                </div>

                {/* GRN History */}
                {pr.grn_entries && pr.grn_entries.length > 0 && (
                  <div className="mt-3 border-t pt-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">Delivery History</p>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs text-center">Qty Received</TableHead>
                          <TableHead className="text-xs">Invoice Ref</TableHead>
                          <TableHead className="text-xs">Delivery Note</TableHead>
                          <TableHead className="text-xs">Recorded By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pr.grn_entries.map((e, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs">{e.received_date || format(new Date(e.created_at), 'dd MMM yyyy')}</TableCell>
                            <TableCell className="text-xs text-center font-semibold text-green-700">{e.quantity_received}</TableCell>
                            <TableCell className="text-xs">{e.invoice_ref || '-'}</TableCell>
                            <TableCell className="text-xs">{e.delivery_note || '-'}</TableCell>
                            <TableCell className="text-xs text-slate-400">{e.recorded_by || e.created_by || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Record Inward Dialog */}
      <Dialog open={!!grnPr} onOpenChange={() => setGrnPr(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Inward — {grnPr?.part_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-slate-50 rounded-md p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">PO / PR No:</span>
                <span className="font-mono">{grnPr?.po_number || grnPr?.pr_number}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-500">Ordered:</span>
                <span>{grnPr?.quantity_ordered || grnPr?.quantity_requested}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-500">Already received:</span>
                <span>{(grnPr?.grn_entries || []).reduce((s, e) => s + (e.quantity_received || 0), 0)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Qty Received *</Label>
                <Input type="number" min="1" className="mt-1 h-8" value={grnForm.quantity_received} onChange={e => setGrnForm(f => ({ ...f, quantity_received: parseInt(e.target.value) || 1 }))} data-testid="grn-qty" />
              </div>
              <div>
                <Label className="text-xs">Received Date *</Label>
                <Input type="date" className="mt-1 h-8" value={grnForm.received_date} onChange={e => setGrnForm(f => ({ ...f, received_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Invoice Ref</Label>
                <Input className="mt-1 h-8" value={grnForm.invoice_ref} onChange={e => setGrnForm(f => ({ ...f, invoice_ref: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Delivery Note</Label>
                <Input className="mt-1 h-8" value={grnForm.delivery_note} onChange={e => setGrnForm(f => ({ ...f, delivery_note: e.target.value }))} />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setGrnPr(null)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleGrn} data-testid="submit-grn">Record Inward</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
